// Projectentabel (standaardweergave), rustig: één regel per project met naam
// (nummer · klant · type eronder), één signaal (rood/oranje, alleen bij een
// probleem), fase, PL, gedeelde tijdas, volgende deadline en uren-%.
// Taken, uren in detail en het productie-stoplicht staan achter "Meer kolommen".
import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { StoplichtRij } from '../../../ui-components/ProjectStoplicht';
import {
  T, Icon, Tip, Signaal, PeriodeBalk, TijdasKop, UrenMeter, TakenTeller,
  VolgendeDeadline, PlAvatar, FaseChip, REGEL_UITLEG,
  PlanningWaarschuwing, projectHref, opGewoneKlik, linkSx,
} from './onderdelen';

// Kolommen: [sleutel, kop, breedte, sorteersleutel?, alleenDetails?]
const KOLOMMEN = [
  ['project', 'Project', 'minmax(220px, 1.6fr)', 'naam'],
  ['signaal', 'Aandacht', '190px', 'risico'],
  ['fase', 'Fase', '104px'],
  ['pl', 'PL', '84px', 'pl'],
  ['periode', 'Periode', 'minmax(140px, 1fr)'],
  ['deadline', 'Volgende deadline', '150px', 'deadline'],
  ['taken', 'Taken', '120px', null, true],
  ['uren', 'Uren', '72px', 'uren'],
  ['productie', 'Productie', '76px', null, true],
  ['meer', '', '28px'],
];

const VERBORGEN_SMAL = new Set(['periode', 'productie', 'fase']);

const kopSx = {
  fontFamily: T.fontBody, fontSize: T.klein, fontWeight: 600,
  whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.25, userSelect: 'none',
};

export default function ProjectTabel({ rijen, venster, sortering, onSorteer, onOpen, stoplichten, details = false, onDetails }) {
  const breed = useMediaQuery('(min-width:1200px)');
  const kolommen = KOLOMMEN
    .filter(([k, , , , alleenDetails]) => (details || !alleenDetails) && (breed || !VERBORGEN_SMAL.has(k)))
    .map((c) => (c[0] === 'uren' && details ? ['uren', 'Uren', '120px', 'uren'] : c));
  const grid = kolommen.map((k) => k[2]).join(' ');

  const kop = (
    <Box sx={{ display: 'grid', gridTemplateColumns: grid, columnGap: 2, alignItems: 'end', px: 2, pt: 1.25, pb: 0.75,
      borderBottom: `1px solid ${T.line}` }}>
      {kolommen.map(([k, label, , sorteer]) => {
        if (k === 'periode') return <Box key={k}><TijdasKop venster={venster} /></Box>;
        if (k === 'meer') {
          return onDetails ? (
            <Tip key={k} title={details ? 'Minder kolommen' : 'Meer kolommen: taken, uren in detail en productie-stoplicht'}>
              <Box role="button" aria-label={details ? 'Minder kolommen' : 'Meer kolommen'} onClick={() => onDetails(!details)}
                sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer', color: details ? T.primary : T.outlineVariant, '&:hover': { color: T.primary } }}>
                <Icon name={details ? 'unfold_less' : 'view_column'} size={16} />
              </Box>
            </Tip>
          ) : <Box key={k} />;
        }
        const actief = sorteer && sortering === sorteer;
        return (
          <Typography key={k} component="div" onClick={sorteer ? () => onSorteer(sorteer) : undefined}
            sx={{ ...kopSx, color: actief ? T.text : T.outline, cursor: sorteer ? 'pointer' : 'default', '&:hover': sorteer ? { color: T.text } : {} }}>
            {label}
            {k === 'signaal' && (
              <Tip title={<Box><Box sx={{ fontWeight: 700, mb: 0.5 }}>Statusregel</Box>{REGEL_UITLEG}</Box>}>
                <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', cursor: 'help' }}><Icon name="info" size={13} sx={{ color: T.outlineVariant }} /></Box>
              </Tip>
            )}
            {actief && <Icon name="arrow_downward" size={12} />}
          </Typography>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', overflowX: 'auto' }}>
      <Box sx={{ minWidth: breed ? 1040 : 720 }}>
      {kop}
      {rijen.map((r) => (
        <Box key={r.id} component="a" href={projectHref(r.id)} onClick={(e) => opGewoneKlik(e, () => onOpen(r))}
          sx={{ ...linkSx, display: 'grid', gridTemplateColumns: grid, columnGap: 2, alignItems: 'center', px: 2, py: 1.1,
            borderBottom: `1px solid ${T.line}`, cursor: 'pointer', minWidth: 0,
            '&:last-of-type': { borderBottom: 'none' },
            '&:hover': { bgcolor: '#f7f9fa' }, '&:focus-visible': { outline: `2px solid ${T.primary}`, outlineOffset: -2 } }}>
          {kolommen.map(([k]) => {
            switch (k) {
              case 'project': return (
                <Box key={k} sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.groot, fontWeight: 600, color: T.text, lineHeight: 1.35, minWidth: 0 }}>{r.naam}</Typography>
                    <PlanningWaarschuwing planning={r.planning} />
                  </Box>
                  <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline, lineHeight: 1.35 }}>
                    {/* Smal scherm: de fasekolom valt weg, dan staat de status hier klein mee. */}
                    {[r.nummer, r.klant, r.type, !breed && r.fase ? (r.fase === 'In progress' ? 'Lopend' : r.fase) : '']
                      .filter(Boolean).join(' · ') || ' '}
                  </Typography>
                </Box>
              );
              case 'signaal': return <Box key={k} sx={{ minWidth: 0 }}>{r.isLead ? null : <Signaal gezondheid={r.gezondheid} />}</Box>;
              case 'fase': return <Box key={k} sx={{ minWidth: 0 }}><FaseChip fase={r.fase} /></Box>;
              case 'pl': return <Box key={k} sx={{ minWidth: 0 }}><PlAvatar namen={r.pls} /></Box>;
              case 'periode': return <Box key={k} sx={{ minWidth: 0 }}>{r.isLead ? null : <PeriodeBalk gezondheid={r.gezondheid} venster={venster} />}</Box>;
              case 'deadline': return <Box key={k} sx={{ minWidth: 0 }}>{r.isLead ? null : <VolgendeDeadline gezondheid={r.gezondheid} />}</Box>;
              case 'taken': return <Box key={k} sx={{ minWidth: 0 }}>{r.isLead ? null : <TakenTeller gezondheid={r.gezondheid} fallbackTotaal={r.takenTotaal} />}</Box>;
              case 'uren': return <Box key={k} sx={{ minWidth: 0, pr: details ? 1 : 0 }}>{r.isLead ? null : <UrenMeter uren={r.gezondheid?.uren} compact kaal={!details} onClick={() => onOpen(r, 'budget')} />}</Box>;
              case 'productie': return <Box key={k}>{r.isLead ? null : <StoplichtRij status={stoplichten[r.id]} size={8} />}</Box>;
              default: return <Box key={k} />;
            }
          })}
        </Box>
      ))}
      </Box>
    </Box>
  );
}
